import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {CollectionProvider} from '../../../model/CollectionProvider';
import {CollectionProviderService} from '../../service-data/collection-provider.service';
import {LanguagePipe} from '../../pipe/language.pipe';

/**
 * Reusable editor for a single {@link CollectionProvider}.
 *
 * Hosted by both `NetworkSettingsComponent` (network role — picks any
 * CollectionProvider by id) and `ServiceProviderSettingsComponent`
 * (serviceProvider role — id is fixed to the SP's own
 * `collectionprovider_ID`). The controller ownership + role gating
 * lives on the server side (see the shared contract); this component
 * only surfaces the CRUD affordances.
 *
 * The `paymentSettings` JSON blob is split into two UI halves:
 *
 *  1. **Known keys** — one labelled input per well-known key
 *     (processor + merchant routing + the five `CARDPOINTE_*`
 *     credentials + optional HTTP timeout). Renders in a fixed order
 *     regardless of which keys the server sent back so the layout is
 *     stable across CollectionProviders. CardPointe API password gets
 *     a password-input mask with a reveal toggle.
 *
 *  2. **Custom keys** — every remaining key/value pair (including
 *     the per-SP `PAYMENT_PROCESSOR_SP_<id>` /
 *     `PAYMENT_MERCHANT_ACCOUNT_SP_<id>` entries, which vary by SP id
 *     so a static labelled row wouldn't scale) shown in a small
 *     add/edit/delete key-value table. New keys can be authored with
 *     the "Add setting" button.
 *
 * On save the two halves are merged back into a single JSON blob and
 * PUT'd via {@link CollectionProviderService.updateCollectionProvider}.
 */
@Component({
  selector: 'app-collection-provider-editor',
  templateUrl: './collection-provider-editor.component.html',
  styleUrls: ['./collection-provider-editor.component.css']
})
export class CollectionProviderEditorComponent implements OnInit, OnChanges {
  /**
   * CollectionProvider id to load. When null the editor renders a
   * "select a provider" placeholder so both hosts can bind their own
   * picker output directly without extra guards.
   */
  @Input() providerId: number | null = null;
  /** When true the editor disables mutations (Save button hidden). */
  @Input() readOnly: boolean = false;

  /** Loaded provider (parsed). Undefined until the first fetch resolves. */
  provider: CollectionProvider | null = null;
  loadState: 'INITIAL' | 'LOADING' | 'LOADED' | 'ERROR' = 'INITIAL';
  loadError: string = '';
  saving: boolean = false;
  saveError: string = '';
  /** True after a successful save; cleared on the next mutation. */
  savedFlash: boolean = false;

  /**
   * Canonical `PAYMENT_PROCESSOR` enum. Sourced from
   * `docs/contracts/shared/PaymentProcessors.md` §2. The server
   * normalizes case-insensitively and accepts a few aliases, but the
   * UI must ONLY store one of these uppercase canonical values.
   *
   * Add / retire values here when a corresponding outbox message
   * updates the shared contract; per §6 of PaymentProcessors.md the
   * server team will send one whenever the enum changes.
   */
  readonly paymentProcessorOptions: Array<{ value: string; label: string; live: boolean }> = [
    { value: 'CARDPOINTE',   label: 'CardPointe / CardConnect', live: true },
    { value: 'AUTHORIZENET', label: 'Authorize.Net (stub — no live charging yet)', live: false },
    { value: 'STRIPE',       label: 'Stripe (stub — no live charging yet)', live: false },
  ];

  /**
   * Ordered list of well-known payment-settings keys. Each entry
   * drives one labelled row in the "Known settings" section.
   *
   * Kept as a plain array so adding a future key is a one-line change
   * — no HTML/CSS/logic modifications needed. Fields flagged
   * `sensitive: true` render as password inputs with a reveal toggle.
   * The optional `type: 'enum'` + `enumValues` render a mat-select
   * instead of a free-text input. The optional `processor` guard
   * hides the field unless `PAYMENT_PROCESSOR` matches (see
   * {@link isKnownFieldVisible}).
   */
  readonly knownKeys: Array<{
    key: string;
    label: string;
    hint?: string;
    sensitive?: boolean;
    type?: 'text' | 'enum';
    /** Only render this field when `PAYMENT_PROCESSOR` matches. */
    processor?: string;
    /**
     * Optional autocomplete attribute override. Defaults to `'off'`
     * in the template. For processor-credential rows (API username +
     * password) we set `'new-password'` — the WHATWG-blessed value
     * for "this is a credential the user is establishing, do NOT
     * offer to save it as a site login and do NOT autofill it from
     * saved logins". `'off'` alone is widely ignored by modern
     * browsers on `type="password"` inputs; `'new-password'` is
     * honoured by Chrome / Firefox / Safari to suppress the "save
     * password?" prompt for the (username, password) pair (see also
     * `data-lpignore` / `data-1p-ignore` / `data-form-type="other"`
     * attributes on the template inputs for the popular password
     * managers).
     */
    autocomplete?: string;
  }> = [
    { key: 'PAYMENT_PROCESSOR', label: 'Payment processor',
      hint: 'Determines which live processor handles charges. Only CARDPOINTE is fully implemented today.',
      type: 'enum' },
    { key: 'CARDPOINTE_GATEWAY_BASE_URL', label: 'CardPointe gateway base URL',
      hint: 'e.g. https://<site>.cardconnect.com', processor: 'CARDPOINTE' },
    { key: 'CARDPOINTE_CARDSECURE_BASE_URL', label: 'CardSecure base URL (optional)',
      hint: 'Optional. Falls back to the gateway base URL when empty.', processor: 'CARDPOINTE' },
    { key: 'CARDPOINTE_API_USERNAME', label: 'CardPointe API username',
      processor: 'CARDPOINTE', autocomplete: 'new-password' },
    { key: 'CARDPOINTE_API_PASSWORD', label: 'CardPointe API password',
      sensitive: true, processor: 'CARDPOINTE', autocomplete: 'new-password' },
    { key: 'CARDPOINTE_HTTP_TIMEOUT_MS', label: 'HTTP timeout (ms, optional)',
      hint: 'Overrides the server default when set.', processor: 'CARDPOINTE' },
  ];

  /**
   * Working copy of the known-key values, keyed by the same `key`
   * strings as {@link knownKeys}. `ngModel`-bound so edits accumulate
   * here until Save merges them back into the JSON blob.
   */
  knownValues: { [key: string]: string } = {};

  /**
   * Working copy of the custom (non-well-known) key/value pairs.
   * Held as an array (rather than an object) so the ngFor template
   * has a stable row identity across edits — an object-keyed ngFor
   * would recycle DOM inputs on rename and the cursor would jump.
   */
  customRows: Array<{ key: string; value: string }> = [];

  /** Per-known-key reveal state for `sensitive: true` fields. */
  revealed: { [key: string]: boolean } = {};

  /**
   * One-time migration warnings surfaced under the known-settings
   * grid when the loaded provider still carries deprecated keys. The
   * warnings are informational; the actual migration happens
   * silently in {@link hydrateWorkingCopy} (renaming) and
   * {@link save} (dropping the deprecated keys from the persisted
   * blob). See inbox messages `2026-07-22-payment-processor-enum-and-fields.md`
   * and `2026-07-22-paymentsettings-drop-cardpointe-merchant-id.md`.
   */
  migrationWarnings: string[] = [];

  constructor(private cpService: CollectionProviderService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['providerId']) { this.load(); }
  }

  /**
   * Fetch the current CollectionProvider from the server. Falls
   * through to a no-op when {@link providerId} is null (the host
   * hasn't picked one yet) so the component can be mounted eagerly
   * without spinning up a request for a nonexistent id.
   */
  load(): void {
    if (this.providerId == null) {
      this.provider = null;
      this.loadState = 'INITIAL';
      return;
    }
    this.loadState = 'LOADING';
    this.loadError = '';
    this.saveError = '';
    this.savedFlash = false;
    this.cpService.getCollectionProvider(this.providerId).subscribe(
      cp => {
        if (!cp) {
          this.loadState = 'ERROR';
          this.loadError = LanguagePipe.get(
            'CollectionProvider not found or you do not have permission to view it.');
          this.provider = null;
          return;
        }
        this.provider = cp;
        this.hydrateWorkingCopy(cp);
        this.loadState = 'LOADED';
      },
      err => {
        console.error('CollectionProviderEditor.load failed', err);
        this.loadState = 'ERROR';
        this.loadError = this.errorMessage(err,
          LanguagePipe.get('Failed to load CollectionProvider.'));
        this.provider = null;
      }
    );
  }

  /**
   * Copy the server-side paymentSettings into the two working buckets
   * (known / custom). Called on every successful load + after every
   * save so the UI never diverges from the persisted state.
   */
  private hydrateWorkingCopy(cp: CollectionProvider): void {
    const settings = cp.getPaymentSettings();
    this.migrationWarnings = [];

    const knownKeySet = new Set(this.knownKeys.map(k => k.key));
    // Also treat the deprecated keys as "known" for hydration purposes
    // so they don't leak into the custom-rows table where an operator
    // could re-save them by accident.
    const deprecatedKeys = new Set<string>(['PAYMENT_PROCESSOR_DEFAULT', 'CARDPOINTE_MERCHANT_ID']);
    this.knownValues = {};
    for (const k of this.knownKeys) {
      const v = settings[k.key];
      // Coerce numbers / booleans back into strings so the form
      // control has one canonical shape. Users typing new values into
      // a string input will produce strings on the way back out.
      this.knownValues[k.key] = v == null ? '' : String(v);
    }
    this.customRows = [];
    for (const key of Object.keys(settings)) {
      if (knownKeySet.has(key)) { continue; }
      if (deprecatedKeys.has(key)) { continue; }
      const v = settings[key];
      this.customRows.push({ key, value: v == null ? '' : String(v) });
    }
    // Stable custom-row order — alphabetical by key so re-loads don't
    // reshuffle the table.
    this.customRows.sort((a, b) => a.key.localeCompare(b.key));
    this.revealed = {};
  }

  /** Add a blank custom-key row and focus it. */
  addCustomRow(): void {
    if (this.effectiveReadOnly) { return; }
    this.customRows.push({ key: '', value: '' });
    this.savedFlash = false;
  }

  /** Remove a custom-key row. */
  removeCustomRow(row: { key: string; value: string }): void {
    if (this.effectiveReadOnly) { return; }
    const idx = this.customRows.indexOf(row);
    if (idx >= 0) { this.customRows.splice(idx, 1); }
    this.savedFlash = false;
  }

  /** Toggle password reveal for a sensitive known-key row. */
  toggleReveal(key: string): void {
    this.revealed[key] = !this.revealed[key];
  }

  /** True when the editor should refuse mutations (input readOnly or no provider). */
  get effectiveReadOnly(): boolean {
    return !!this.readOnly || !this.provider;
  }

  /** Current PAYMENT_PROCESSOR working-copy value (uppercased for compare). */
  get selectedProcessor(): string {
    return (this.knownValues['PAYMENT_PROCESSOR'] || '').toUpperCase();
  }

  /**
   * Visibility gate for a known-key row. Processor-scoped fields
   * (e.g. every `CARDPOINTE_*` credential) are only rendered when
   * the selected `PAYMENT_PROCESSOR` matches. Fields without a
   * `processor` guard are always visible. If the underlying value is
   * already set we keep the row visible regardless of the processor
   * selection so a mistaken enum switch doesn't hide non-empty
   * credentials the operator might want to review before saving.
   */
  isKnownFieldVisible(field: { key: string; processor?: string }): boolean {
    if (!field.processor) { return true; }
    if (this.selectedProcessor === field.processor) { return true; }
    // Preserve non-empty values so operators can see what would be
    // dropped on save (see also the save() filter that strips these).
    const v = (this.knownValues[field.key] || '').trim();
    return v.length > 0;
  }

  /**
   * Any-processor "always shown" test — a merchant-account row is
   * required regardless of processor. Used by the required-field
   * validator in {@link save}.
   */
  private hasAnyMerchantAccount(merged: { [key: string]: any }): boolean {
    for (const k of Object.keys(merged)) {
      if (k.indexOf('PAYMENT_MERCHANT_ACCOUNT_SP_') === 0
          && merged[k] && String(merged[k]).trim().length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Merge the two working buckets back into a single JSON blob and
   * PUT via the service. Only sends the CollectionProvider row —
   * server owns Name / admin_ID / lastUpdate rehydration.
   */
  save(): void {
    if (this.effectiveReadOnly || !this.provider || this.saving) { return; }
    this.saving = true;
    this.saveError = '';
    this.savedFlash = false;
    // Build a fresh settings object. Known keys go first (in
    // knownKeys order) so the persisted JSON is byte-stable across
    // saves — helps future signature-based change detection and
    // makes on-disk diffs easier to review.
    //
    // Processor-scoped keys (fields carrying a `processor` guard) are
    // only persisted when the selected `PAYMENT_PROCESSOR` matches.
    // This keeps the blob tidy when an operator switches processors
    // and prevents stale credentials for the un-selected processor
    // from lingering in the JSON.
    const processor = this.selectedProcessor;
    const merged: { [key: string]: any } = {};
    for (const k of this.knownKeys) {
      const raw = (this.knownValues[k.key] || '').trim();
      if (raw.length === 0) { continue; }
      if (k.processor && k.processor !== processor) { continue; }
      merged[k.key] = raw;
    }
    // Custom keys — trim keys, drop rows with an empty key. Duplicate
    // keys (custom-vs-custom or custom-vs-known) resolve by "last
    // write wins" in insertion order; the sort below happens after
    // dedup so the UI's presentation order isn't misleading.
    const seenCustomKeys = new Set<string>(Object.keys(merged));
    const duplicates: string[] = [];
    const deprecatedFromCustom: string[] = [];
    for (const row of this.customRows) {
      const key = (row.key || '').trim();
      if (!key) { continue; }
      // Refuse to persist deprecated keys the operator may have typed
      // by hand — server doesn't read them any more. Silent-drop with
      // a save-error return so the operator learns why.
      if (key === 'PAYMENT_PROCESSOR_DEFAULT' || key === 'CARDPOINTE_MERCHANT_ID') {
        deprecatedFromCustom.push(key);
        continue;
      }
      if (seenCustomKeys.has(key)) { duplicates.push(key); continue; }
      seenCustomKeys.add(key);
      merged[key] = (row.value || '').trim();
    }
    if (duplicates.length > 0) {
      this.saving = false;
      this.saveError = LanguagePipe.get('Duplicate setting key:')
        + ' ' + duplicates.join(', ');
      return;
    }
    if (deprecatedFromCustom.length > 0) {
      this.saving = false;
      this.saveError = LanguagePipe.get('These keys are deprecated and cannot be saved:')
        + ' ' + deprecatedFromCustom.join(', ');
      return;
    }
    // Validation: when a live processor is selected the server will
    // reject charges without a merchant account. Fail closed here so
    // the operator gets an immediate, understandable error instead of
    // a later PAYMENT_CONFIG_ERROR from the server at charge time.
    if (processor === 'CARDPOINTE' && !this.hasAnyMerchantAccount(merged)) {
      this.saving = false;
      this.saveError = LanguagePipe.get('PAYMENT_MERCHANT_ACCOUNT_SP_<id> override) is required when PAYMENT_PROCESSOR.');
      return;
    }
    // Round-trip through the model helper so any future encoding
    // policy (e.g. server-side signature stamp) has a single hook.
    this.provider.setPaymentSettings(merged);
    this.cpService.updateCollectionProvider(this.provider).subscribe(
      updated => {
        this.saving = false;
        if (!updated) {
          this.saveError = LanguagePipe.get('Save failed. Please try again.');
          return;
        }
        // Re-hydrate from the server-echoed row so any server-side
        // transformation (e.g. dropped unknown keys) reflects in the
        // UI immediately.
        this.provider = new CollectionProvider(updated);
        this.hydrateWorkingCopy(this.provider);
        this.savedFlash = true;
      },
      err => {
        this.saving = false;
        console.error('CollectionProviderEditor.save failed', err);
        this.saveError = this.errorMessage(err,
          LanguagePipe.get('Save failed. Please try again.'));
      }
    );
  }

  private errorMessage(err: any, fallback: string): string {
    if (err && err.error && typeof err.error === 'object' && err.error.error) {
      return String(err.error.error);
    }
    if (err && err.message) { return String(err.message); }
    return fallback;
  }
}

